import * as Actions from './Actions'
import * as UnifiedActions from './unified-actions'
import { put, post } from '../../calculator/lib/xhr'
import { INSTRUMENTATION_TYPES, ActionTypes as types } from '../constants'
import { INSTRUMENT_TYPES } from '../../../../lib/constants'
import * as travelRuleUtils from '../utils/travelRuleUtils'
import { routeTo } from '../utils/routeHelper'
import { getUrlFragment } from '../utils/url-utils'
import {
  receivedFundingOptions,
  redirectToAddFI,
  redirectSenderCipVerification,
  routeReturningUserFromCip,
} from './send'
import { collectBrowserContext } from '../utils/3ds-utils'
import { calculateAttributesToCollect } from '../utils/travelRuleUtils'
import { getSessionDataPayload } from 'transfer/utils/session'
import { isInstrumentDebitFailedError } from '../utils/fiFailureUtils'
import { rankingPreferencesHelper } from './sendHelpers'
import { resolvePaymentContingenciesSync } from './payment-contingency/paymentContingencyDispatchers'
import { blockPayment } from './payment-contingency/paymentContingencyActions'

export function fetchFundingOptions({
  conversionType = null,
  handleFISelectorNext = null,
  preselectedFundingOption = {},
  instrumentIds = [],
} = {}) {
  // prettier-ignore
  return (dispatch, getState) => { // NOSONAR
    const {
      server: {
        amount,
        requestId,
        requestLegId,
        recipient,
        recipientFullName,
        recipientCountry,
        currencyCode,
      },
      address: {
        selectedShippingAddress: { id: shippingAddressId },
      },
      transaction: {
        isPurchase: goods,
        selectedFundingOption: { funding_sources },
        successfullyAddedFundingInstrumentIds,
        isSenderReturningFromCIP,
        selectedPurposeOfPaymentOption,
      },
      recipient: { token },
      note: { note },
      user: { requireSenderInstrumentAddition = false },
    } = getState()

    if (requireSenderInstrumentAddition) {
      // no available FIs to use for the current transaction, send user to wallet
      dispatch(Actions.setIsLoading(true))
      return dispatch(redirectToAddFI())
    }

    dispatch({ type: types.TRANSACTION.FETCH_FUNDING_OPTIONS })

    let paymentCardId

    if (conversionType) {
      const paymentCard =
        funding_sources &&
        funding_sources.find(
          source => source.instrument_type === INSTRUMENT_TYPES.PAYMENT_CARD
        )
      paymentCardId = paymentCard && paymentCard.id
    }

    /*
     * We need to pass the wallet success ID to planning call so that
     * service does a refresh of the FIs (cache busting) or else a
     * 'SENDER_INSTRUMENTS_UNAVAILABLE' error is thrown.
     *
     * Normal external calls work in homepage-external-redirect.js by parsing
     * and passing it directly to the FI call, but payRequest does the FI
     * call here.
     */
    const fundingInstrumentIds = getSuccessfullyAddedFundingInstrumentIds(
      instrumentIds,
      successfullyAddedFundingInstrumentIds
    )

    // For mandatory FI, generate additional params for funding_option call
    const preferences = rankingPreferencesHelper({
      conversionType,
      instrumentIds: fundingInstrumentIds,
      preselectedFundingOption,
    })

    const browserData = collectBrowserContext()
    const browserContext = {
      screenHeight: browserData.browser_screen_height,
      screenWidth: browserData.browser_screen_width,
      colorDepth: browserData.browser_color_depth,
      timezoneOffsetMinutes: browserData.browser_timezone_offset_minutes,
      language: browserData.browser_language,
      javaEnabled: browserData.browser_java_enabled,
    }

    const payload = {
      recipient,
      amount,
      currencyCode,
      goods,
      shippingAddressId,
      recipientFullName,
      recipientCountry,
      paymentCardId,
      conversionType,
      successfullyAddedFundingInstrumentIds,
      token,
      browserContext,
      note,
      ...(selectedPurposeOfPaymentOption?.code && {
        purposeCode: selectedPurposeOfPaymentOption?.code,
      }),
      ...getSessionDataPayload(),
    }

    if (preferences) {
      payload.preferences = preferences
    }

    const url = `/myaccount/transfer/payRequest/${requestId}/${requestLegId}/fundingOptions`
    post(url, payload).then(resolvePaymentContingenciesSync(dispatch)) // callback returns another function that receives and returns the response
      .then(response => {
        const { data: responseData = {} } = response
        responseData.conversionType = payload.conversionType

        if (responseData.hasSenderCipVerificationContingency) {
          if (isSenderReturningFromCIP) {
            return dispatch(routeReturningUserFromCip())
          }
          return dispatch(
            redirectSenderCipVerification({
              senderCipVerification: responseData.senderCipVerification,
              hasSenderCipVerificationContingency:
                responseData.hasSenderCipVerificationContingency,
            })
          )
        }

        dispatch(receivedFundingOptions(responseData))

        if (handleFISelectorNext) {
          handleFISelectorNext(dispatch)
        }
      })
      .catch(err => {
        switch (err.code) {
          case 'noFundingSource':
            return dispatch(Actions.noFundingSource())

          case 'authRequired':
            return // do nothing bc we are redirecting the user to auth flow in xhr.js

          case 'paymentBlocked':
            return dispatch(blockPayment())

          case 'contingencyRejection':
            return // don't throw an error, just don't continue with transaction.

          default:
            return dispatch(Actions.showError(err))
        }
      })
  }
}

export function handlePayRequestSubmit() {
  return (dispatch, getState) => {
    // grab necessary data for pay request from store
    const {
      server: { requestId, requestLegId, country },
      transaction: {
        selectedFundingOption,
        selectedFundingOption: { id: fundingOptionId },
        selectedInstallmentPlan: { installmentTerm },
      },
      note: { note: description },
      user,
    } = getState()

    // For v1, installmentTerm is a Number, but should be string for ajax call to server
    // For v2, installmentTerm is in Iso8601 format as Service only accepts duration in that format
    const installment_term = installmentTerm && installmentTerm.toString()

    const payRequestData = {
      requestId,
      requestLegId,
      fundingOptionId,
      description,
      installment_term,
      travelRuleRequirements: calculateAttributesToCollect(
        selectedFundingOption
      ),
    }

    if (!travelRuleUtils.isTravelRule(selectedFundingOption)) {
      return dispatch(payRequest(payRequestData))
    }

    if (
      travelRuleUtils.hasAllNeededTravelRuleInfo({
        ...user,
        selectedFundingOption,
      })
    ) {
      const travelRuleData = travelRuleUtils.massageTravelRuleDataForPost({
        ...user,
        selectedFundingOption,
        country,
      })
      return dispatch(
        postTravelRuleAndPayRequest({ ...payRequestData, ...travelRuleData })
      )
    }

    // if travel rule but not all info is present, just return out
    return
  }
}

export function payRequest(data) {
  return dispatch => {
    dispatch({ type: types.TRANSACTION.PAY_REQUEST })
    const url = `/myaccount/transfer/payRequest/${data.requestId}/${data.requestLegId}`
    const params = {
      ...data,
      id: data.requestId,
      legId: data.requestLegId,
    }

    put(url, params)
      .then(response => {
        dispatch(paidRequest(response.data))
        routeTo(`${getUrlFragment()}/success`)
        Actions.resetScrollPosition()
      })
      .catch(err => {
        if (isInstrumentDebitFailedError(err)) {
          return dispatch(
            UnifiedActions.fetchFundingOptionsAndShowFundingOptionsOverpanel()
          )
        }

        return Actions.handleError(dispatch)(err)
      })
  }
}

/**
 * Post new travel rule data, then pay the request
 * @param {}
 * @return {}
 */
export function postTravelRuleAndPayRequest(data) {
  return dispatch => {
    dispatch({ type: types.TRANSACTION.POST_TRAVEL_RULE_DATA })
    dispatch({ type: types.TRANSACTION.PAY_REQUEST })
    const url = `/myaccount/transfer/payRequest/${data.requestId}/${data.requestLegId}/travelRule`
    const payload = {
      ...data,
      id: data.requestId,
      legId: data.requestLegId,
    }

    return put(url, payload)
      .then(response => {
        dispatch({ type: types.TRANSACTION.POSTED_TRAVEL_RULE_DATA })
        dispatch(paidRequest(response.data))
        routeTo(`${getUrlFragment()}/success`)
      })
      .catch(err => {
        if (isInstrumentDebitFailedError(err)) {
          return dispatch(
            UnifiedActions.fetchFundingOptionsAndShowFundingOptionsOverpanel()
          )
        }

        return Actions.handleError(dispatch)(err)
      })
  }
}

export function paidRequest({ payment_details = {}, compliance_info = {} }) {
  const { payment_id, amount: { value } = {} } = payment_details

  const data = {
    trid: payment_id,
    txn_amt: value,
  }

  return {
    type: types.TRANSACTION.PAID_REQUEST,
    meta: Actions.getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
      linkName: 'paidRequestSuccessful',
      data,
    }),
    data: {
      paymentDetails: payment_details,
      complianceInfo: compliance_info,
    },
    receivedAt: Date.now(),
  }
}

export function getSuccessfullyAddedFundingInstrumentIds(
  instrumentIds,
  successfullyAddedFundingInstrumentIds
) {
  let ids = instrumentIds

  if (
    successfullyAddedFundingInstrumentIds &&
    typeof successfullyAddedFundingInstrumentIds === 'string'
  ) {
    ids = successfullyAddedFundingInstrumentIds.split(',')
  }

  return ids
}
